import { useState, useRef, useEffect } from 'react';
import { FORM_CONFIG } from '../config/formConfig';
import { useRecaptcha } from '../hooks/useRecaptcha';

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'bot';
  timestamp: Date;
  options?: string[];
  type?: 'text' | 'options';
}

type FormStep = 'idle' | 'name' | 'email' | 'phone' | 'program' | 'message' | 'complete';

const PROGRAMS = [
  'CCM - Chronic Care Management',
  'RPM - Remote Patient Monitoring',
  'AWV - Annual Wellness Visit',
  'BHI - Behavioral Health Integration',
  'PCM - Principal Care Management',
  'TCM - Transitional Care Management',
  'RTM - Remote Therapeutic Monitoring',
  'CPM - Chronic Pain Management',
  'General Inquiry'
];

export function ChatBot() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      text: "Hello! I'm your AI assistant. How can I help you today?",
      sender: 'bot',
      timestamp: new Date(),
      type: 'text'
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [formStep, setFormStep] = useState<FormStep>('idle');
  const [isAskingForConsult, setIsAskingForConsult] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    program: '',
    message: ''
  });
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Initialize reCAPTCHA
  const { executeRecaptcha } = useRecaptcha({
    siteKey: FORM_CONFIG.RECAPTCHA_SITE_KEY,
    action: 'chatbot_consultation'
  });

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current && formStep !== 'program') {
      inputRef.current.focus();
    }
  }, [isOpen, formStep]);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone: string): boolean => {
    const phoneRegex = /^[\d\s\(\)\-\+]{10,}$/;
    return phoneRegex.test(phone.replace(/\s/g, ''));
  };

  const startConsultDemo = () => {
    setIsAskingForConsult(false);
    setFormStep('name');
    setFormData({ name: '', email: '', phone: '', program: '', message: '' });
    const welcomeMessage: Message = {
      id: Date.now().toString(),
      text: "Great! I'll help you schedule a consultation or demo. Let's start by collecting some information.",
      sender: 'bot',
      timestamp: new Date(),
      type: 'text'
    };
    const nameMessage: Message = {
      id: (Date.now() + 1).toString(),
      text: "What's your full name?",
      sender: 'bot',
      timestamp: new Date(),
      type: 'text'
    };
    setMessages(prev => [...prev, welcomeMessage, nameMessage]);
  };

  const handleOptionSelect = (option: string) => {
    if (formStep === 'program') {
      setFormData(prev => ({ ...prev, program: option }));
      const userMessage: Message = {
        id: Date.now().toString(),
        text: option,
        sender: 'user',
        timestamp: new Date(),
        type: 'text'
      };
      setMessages(prev => [...prev, userMessage]);
      
      setIsTyping(true);
      setTimeout(() => {
        const botMessage: Message = {
          id: (Date.now() + 1).toString(),
          text: `Great! You selected ${option}. Is there anything specific you'd like to tell us? (This is optional - you can type "skip" or "no" to continue)`,
          sender: 'bot',
          timestamp: new Date(),
          type: 'text'
        };
        setMessages(prev => [...prev, botMessage]);
        setIsTyping(false);
        setFormStep('message');
      }, 1000);
    }
  };

  const processFormStep = (userInput: string) => {
    const trimmedInput = userInput.trim();

    switch (formStep) {
      case 'name':
        if (trimmedInput.length < 2) {
          const errorMsg: Message = {
            id: Date.now().toString(),
            text: "Please provide a valid name (at least 2 characters).",
            sender: 'bot',
            timestamp: new Date(),
            type: 'text'
          };
          setMessages(prev => [...prev, errorMsg]);
          return;
        }
        setFormData(prev => ({ ...prev, name: trimmedInput }));
        setIsTyping(true);
        setTimeout(() => {
          const botMessage: Message = {
            id: Date.now().toString(),
            text: `Nice to meet you, ${trimmedInput}! What's your email address?`,
            sender: 'bot',
            timestamp: new Date(),
            type: 'text'
          };
          setMessages(prev => [...prev, botMessage]);
          setIsTyping(false);
          setFormStep('email');
        }, 1000);
        break;

      case 'email':
        if (!validateEmail(trimmedInput)) {
          const errorMsg: Message = {
            id: Date.now().toString(),
            text: "Please provide a valid email address (e.g., name@example.com).",
            sender: 'bot',
            timestamp: new Date(),
            type: 'text'
          };
          setMessages(prev => [...prev, errorMsg]);
          return;
        }
        setFormData(prev => ({ ...prev, email: trimmedInput }));
        setIsTyping(true);
        setTimeout(() => {
          const botMessage: Message = {
            id: Date.now().toString(),
            text: "Perfect! What's your phone number?",
            sender: 'bot',
            timestamp: new Date(),
            type: 'text'
          };
          setMessages(prev => [...prev, botMessage]);
          setIsTyping(false);
          setFormStep('phone');
        }, 1000);
        break;

      case 'phone':
        if (!validatePhone(trimmedInput)) {
          const errorMsg: Message = {
            id: Date.now().toString(),
            text: "Please provide a valid phone number (at least 10 digits).",
            sender: 'bot',
            timestamp: new Date(),
            type: 'text'
          };
          setMessages(prev => [...prev, errorMsg]);
          return;
        }
        setFormData(prev => ({ ...prev, phone: trimmedInput }));
        setIsTyping(true);
        setTimeout(() => {
          const botMessage: Message = {
            id: Date.now().toString(),
            text: "Which program are you interested in? Please select one:",
            sender: 'bot',
            timestamp: new Date(),
            type: 'options',
            options: PROGRAMS
          };
          setMessages(prev => [...prev, botMessage]);
          setIsTyping(false);
          setFormStep('program');
        }, 1000);
        break;

      case 'message':
        const finalMessage = (trimmedInput.toLowerCase() === 'skip' || trimmedInput.toLowerCase() === 'no' || trimmedInput === '') ? '' : trimmedInput;
        const finalFormData = { ...formData, message: finalMessage };
        setFormData(finalFormData);
        setIsTyping(true);
        
        // Submit to email endpoint
        const submitForm = async () => {
          try {
            //console.log(FORM_CONFIG.RECAPTCHA_SITE_KEY)
            // Execute reCAPTCHA
            let recaptchaToken = null;
            if (FORM_CONFIG.RECAPTCHA_SITE_KEY) {
              recaptchaToken = await executeRecaptcha();
              if (!recaptchaToken) {
                console.warn('reCAPTCHA token not obtained, but continuing with submission');
              }
            }

            const response = await fetch(FORM_CONFIG.NODE_FORM_URL, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                ...finalFormData,
                source: 'ChatBot Consultation',
                recaptchaToken: recaptchaToken
              })
            });

            if (response.ok) {
              await response.json();
              // console.log('=== CONSULTATION/DEMO FORM DATA SUBMITTED ===');
              // console.log('Name:', finalFormData.name);
              // console.log('Email:', finalFormData.email);
              // console.log('Phone:', finalFormData.phone);
              // console.log('Program:', finalFormData.program);
              // console.log('Message:', finalFormData.message || '(No message provided)');
              // console.log('Timestamp:', new Date().toISOString());
              // console.log('Status: Successfully submitted to email endpoint');
              // console.log('===========================================');
            } else {
              const errorData = await response.json().catch(() => ({}));
              //console.error('Form submission failed:', response.status, errorData);
              // console.log('=== CONSULTATION/DEMO FORM DATA (Fallback) ===');
              // console.log('Name:', finalFormData.name);
              // console.log('Email:', finalFormData.email);
              // console.log('Phone:', finalFormData.phone);
              // console.log('Program:', finalFormData.program);
              // console.log('Message:', finalFormData.message || '(No message provided)');
              // console.log('Timestamp:', new Date().toISOString());
              // console.log('==============================================');
            }
          } catch (error) {
            // console.error('Form submission error:', error);
            // console.log('=== CONSULTATION/DEMO FORM DATA (Error Fallback) ===');
            // console.log('Name:', finalFormData.name);
            // console.log('Email:', finalFormData.email);
            // console.log('Phone:', finalFormData.phone);
            // console.log('Program:', finalFormData.program);
            // console.log('Message:', finalFormData.message || '(No message provided)');
            // console.log('Timestamp:', new Date().toISOString());
            // console.log('==================================================');
          }
        };

        submitForm();

        setTimeout(() => {
          const botMessage: Message = {
            id: Date.now().toString(),
            text: "Thank you for providing your information! I've collected all the details. Our team will contact you within 24 hours.",
            sender: 'bot',
            timestamp: new Date(),
            type: 'text'
          };
          setMessages(prev => [...prev, botMessage]);
          setIsTyping(false);
          setIsAskingForConsult(false);
          setFormStep('complete');
        }, 1000);
        break;
    }
  };

  const generateAIResponse = (userMessage: string): { text: string; shouldOfferConsult?: boolean } => {
    const message = userMessage.toLowerCase().trim();

    // Greetings
    if (message.match(/^(hi|hello|hey|greetings|good morning|good afternoon|good evening)/)) {
      return { text: "Hello! I'm here to help you learn about our chronic care management services. What would you like to know?" };
    }

    // CCM related
    if (message.match(/(ccm|chronic care|chronic care management)/)) {
      return { text: "CCM (Chronic Care Management) helps manage patients with two or more chronic conditions. We provide comprehensive care coordination, medication management, and 24/7 monitoring. Would you like to know more about our CCM program?" };
    }

    // RPM related
    if (message.match(/(rpm|remote patient monitoring|remote monitoring)/)) {
      return { text: "RPM (Remote Patient Monitoring) allows us to monitor patients' health data remotely using medical devices. This helps catch issues early and improve outcomes. Would you like information about our RPM services?" };
    }

    // Pricing/Cost
    if (message.match(/(price|cost|pricing|how much|fee|revenue|reimbursement)/)) {
      return { text: "Our services are typically covered by Medicare and most insurance plans. Practices can generate $40+ monthly revenue per patient. Would you like to schedule a consultation to discuss pricing for your practice?", shouldOfferConsult: true };
    }

    // Implementation/Timeline
    if (message.match(/(implement|implementation|timeline|how long|start|begin|setup|get started)/)) {
      return { text: "Implementation is fast - typically 3-4 weeks from partnership to full launch. We handle everything including EMR integration, staff training, and patient enrollment. Ready to get started?", shouldOfferConsult: true };
    }

    // Contact/Consultation/Demo - trigger consult demo offer
    if (message.match(/(contact|phone|email|call|reach|speak|talk|demo|consultation|schedule|book|appointment|meeting|consult|request demo|request consultation)/)) {
      return { text: "You can reach us at (844) iCCM MED or email emma@integratedccm.com. Would you like to schedule a consultation or demo? I can help you with that!", shouldOfferConsult: true };
    }

    // Programs
    if (message.match(/(program|programs|services|what do you offer|offerings)/)) {
      return { text: "We offer 8 comprehensive programs: CCM, RPM, AWV, BHI, PCM, TCM, RTM, and CPM. Each program addresses specific patient needs and is designed to improve outcomes while generating revenue for your practice." };
    }

    // Benefits
    if (message.match(/(benefit|benefits|why|advantage|advantages|help|improve)/)) {
      return { text: "Our services help reduce readmissions by 35%, decrease ER visits by 24%, and improve patient outcomes. Plus, you can generate additional revenue while providing better care. Would you like details on any specific program?" };
    }

    // Technology/Platform
    if (message.match(/(platform|technology|system|software|emr|integration|tech)/)) {
      return { text: "Our iCCM platform integrates seamlessly with your existing EHR system. No complicated technology to learn - we use your existing systems with single sign-on and dynamic data syncing." };
    }

    // Team/Support
    if (message.match(/(team|support|staff|help|assistance|care team|coordinators)/)) {
      return { text: "We provide a multidisciplinary care team including internal medicine experts, pharmacy doctors, nurses, and specialists. Our team works alongside yours to deliver comprehensive care." };
    }

    // Default responses
    const defaultResponses = [
      "That's a great question! Our chronic care management services help practices improve patient outcomes while generating additional revenue. Would you like to know more about our programs?",
      "I'd be happy to help! We offer comprehensive chronic care management solutions. What specific information are you looking for?",
      "Let me help you with that. We provide various care management programs including CCM, RPM, and more. What would you like to learn about?",
      "Great question! You can learn more about our services or schedule a consultation. Would you like me to help you get started?"
    ];

    return { text: defaultResponses[Math.floor(Math.random() * defaultResponses.length)] };
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim()) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      text: inputValue,
      sender: 'user',
      timestamp: new Date(),
      type: 'text'
    };

    setMessages(prev => [...prev, userMessage]);
    const currentInput = inputValue;
    setInputValue('');
    
    // If we're in form collection mode, process the form step
    if (formStep !== 'idle' && formStep !== 'complete') {
      processFormStep(currentInput);
      return;
    }

    // Check if user is responding to consult demo offer
    if (isAskingForConsult) {
      const response = currentInput.toLowerCase().trim();
      if (response.match(/^(yes|yeah|yep|yup|sure|ok|okay|of course|absolutely|definitely|i would|i'd like|let's do it|start|begin)/)) {
        setIsAskingForConsult(false);
        startConsultDemo();
        return;
      } else {
        setIsAskingForConsult(false);
        setIsTyping(true);
        setTimeout(() => {
          const botResponse: Message = {
            id: (Date.now() + 1).toString(),
            text: "No problem! Feel free to ask me anything else about our services.",
            sender: 'bot',
            timestamp: new Date(),
            type: 'text'
          };
          setMessages(prev => [...prev, botResponse]);
          setIsTyping(false);
        }, 1000);
        return;
      }
    }

    // Otherwise, generate AI response
    setIsTyping(true);
    setTimeout(() => {
      const aiResponse = generateAIResponse(currentInput);
      const botResponse: Message = {
        id: (Date.now() + 1).toString(),
        text: aiResponse.text,
        sender: 'bot',
        timestamp: new Date(),
        type: 'text'
      };
      setMessages(prev => [...prev, botResponse]);
      
      // If AI suggests consult demo, ask for confirmation
      if (aiResponse.shouldOfferConsult && formStep === 'idle') {
        setIsAskingForConsult(true);
        setTimeout(() => {
          const consultOffer: Message = {
            id: (Date.now() + 2).toString(),
            text: "Would you like me to help you schedule a consultation or demo? Just say 'yes' and I'll collect your information!",
            sender: 'bot',
            timestamp: new Date(),
            type: 'text'
          };
          setMessages(prev => [...prev, consultOffer]);
        }, 500);
      }
      
      setIsTyping(false);
    }, 1000 + Math.random() * 1000);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  const resetChat = () => {
    setMessages([
      {
        id: '1',
        text: "Hello! I'm your AI assistant. How can I help you today?",
        sender: 'bot',
        timestamp: new Date(),
        type: 'text'
      }
    ]);
    setFormStep('idle');
    setFormData({ name: '', email: '', phone: '', program: '', message: '' });
  };

  return (
    <>
      {/* Floating Chat Button */}
      <button
        onClick={() => {
          setIsOpen(!isOpen);
          if (!isOpen) {
            resetChat();
          }
        }}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br from-teal-700 to-cyan-700 text-white rounded-full shadow-2xl hover:shadow-teal-500/50 hover:scale-110 active:scale-95 transition-all duration-300 flex items-center justify-center group"
        aria-label="Open chat"
      >
        {!isOpen ? (
          <i className="ri-message-3-line text-2xl"></i>
        ) : (
          <i className="ri-close-line text-2xl"></i>
        )}
        {!isOpen && (
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full border-2 border-white animate-pulse"></span>
        )}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-20 right-4 left-4 md:bottom-24 md:right-6 md:left-auto z-50 w-auto md:w-full md:max-w-md h-[calc(100vh-100px)] md:h-[600px] md:max-h-[calc(100vh-120px)] bg-white rounded-2xl shadow-2xl flex flex-col border-2 border-teal-100 overflow-hidden">
          {/* Chat Header */}
          <div className="bg-gradient-to-r from-teal-700 to-cyan-700 text-white p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <i className="ri-robot-line text-xl"></i>
              </div>
              <div>
                <h3 className="font-semibold text-lg">iCCM Assistant</h3>
                <p className="text-xs text-teal-50">AI-powered support</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="w-8 h-8 flex items-center justify-center hover:bg-white/20 rounded-full transition-colors"
              aria-label="Close chat"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>

          {/* Messages Container */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gradient-to-b from-gray-50 to-white">
            {messages.map((message) => (
              <div key={message.id}>
                <div
                  className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                      message.sender === 'user'
                        ? 'bg-gradient-to-r from-teal-700 to-cyan-700 text-white'
                        : 'bg-white text-gray-800 shadow-md border border-gray-200'
                    }`}
                  >
                    <p className="text-sm leading-relaxed">{message.text}</p>
                    <span
                      className={`text-xs mt-1 block ${
                        message.sender === 'user' ? 'text-teal-50' : 'text-gray-500'
                      }`}
                    >
                      {formatTime(message.timestamp)}
                    </span>
                  </div>
                </div>
                
                {/* Option Buttons */}
                {message.type === 'options' && message.options && (
                  <div className="mt-3 flex flex-col gap-2">
                    {message.options.map((option, index) => (
                      <button
                        key={index}
                        onClick={() => handleOptionSelect(option)}
                        className="text-left px-4 py-3 bg-white border-2 border-teal-200 rounded-xl hover:border-teal-500 hover:bg-teal-50 transition-all text-sm font-medium text-gray-700 shadow-sm"
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
            
            {isTyping && (
              <div className="flex justify-start">
                <div className="bg-white text-gray-800 shadow-md border border-gray-200 rounded-2xl px-4 py-3">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></span>
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></span>
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div className="border-t border-gray-200 p-4 bg-white">
            {/* Consult Demo Button - Show when idle or chatting (not in form collection) */}
            {(formStep === 'idle' || formStep === 'complete') && (
              <div className="mb-3">
                <button
                  onClick={startConsultDemo}
                  className="w-full bg-gradient-to-r from-teal-700 to-cyan-700 text-white px-6 py-3 rounded-xl hover:from-teal-800 hover:to-cyan-800 transition-all font-semibold text-sm shadow-lg flex items-center justify-center gap-2"
                >
                  <i className="ri-calendar-check-line"></i>
                  Consult Demo
                </button>
              </div>
            )}
            
            <div className="flex gap-2">
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={formStep === 'program' ? "Select a program above..." : "Type your message..."}
                disabled={formStep === 'program'}
                className="flex-1 px-4 py-3 rounded-xl border-2 border-gray-300 focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none transition-all text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
              <button
                onClick={handleSendMessage}
                disabled={!inputValue.trim() || isTyping || formStep === 'program'}
                className="w-12 h-12 bg-gradient-to-r from-teal-700 to-cyan-700 text-white rounded-xl hover:from-teal-800 hover:to-cyan-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center shadow-lg"
                aria-label="Send message"
              >
                <i className="ri-send-plane-fill text-xl"></i>
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2 text-center">
              {formStep === 'program' 
                ? 'Please select a program from the options above' 
                : 'Press Enter to send • AI-powered responses'}
            </p>
          </div>
        </div>
      )}
    </>
  );
}
